import React, { useState, useEffect, memo } from "react";
import { Text, StyleSheet, View, TouchableOpacity, Alert, Platform } from "react-native";
import Swipeable from "../../../../lib/react-native-swipeable";
import { useNavigation } from '@react-navigation/native';

import RoundCheckbox from "../../common/RoundCheckbox";

import {
  isWeb,
  getActivityModel,
  isReflection,
  isFitness, getElementModel,
} from "../../../../helpers/utils";

import ActivityTags from "./ActivityTags";
import ActivitySource from "../../journal/timeslots/ActivitySource";
import ImageComponent from "../../common/ImageComponent";
import Icons from "../../common/icons/index";
import TutorialsHelper from "../../tutorials/TutorialsHelper";
import appStyles from "../../../appStyles";

const ActivityItem = ({
  item,
  controller,
  showSelector = false,
  onOpen = () => {},
  onClose = () => {},
  onPredictionIconPressed = () => {},
  onActivitySelected = () => {},
  showEditOptions,
}) => {
  const [showAllTags, setShowAllTags] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isSelectable, setIsSelectable] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const navigation = useNavigation();

  useEffect(() => {
    setIsFavorite(item.is_favorite == 1);
    setIsSelected(item.is_selected);
    setIsSelectable(!!showSelector);
    setIsArchived(!item.is_active);
  }, []);

  useEffect(() => {
    if (controller?.state?.showTutorials) {
      const showGoalTutorials = (controller?.state?.showTutorials || {})[
        TutorialsHelper.STORAGE_KEYS.goals
      ];

      const showTrackTutorials = (controller?.state?.showTutorials || {})[
        TutorialsHelper.STORAGE_KEYS.track
      ];

      if (showGoalTutorials) {
        setIsHighlighted(isReflection(item.name));
      }

      if (showTrackTutorials) {
        setIsHighlighted(isFitness(item.name));
      }
    }
  }, [controller?.state?.showTutorials]);

  const onActivitySelectedHandler = () => {
    if (isSelectable) {
      setIsSelected(!isSelected);
    }

    const source = "edit";
    onActivitySelected(
      {
        ...item,
        isHighlighted,
      },
      !isSelected,
      false,
      false,
      source,
    );
  };

  const onLongPress = (e) => {
    e.preventDefault();

    if (showEditOptions) {
      showEditOptions(item);
    } else {
      setShowAllTags(true);
    }
  };

  const toggleFavoriteState = () => {
    setIsFavorite(!isFavorite);

    controller.tracking.track("Edit Element");
    controller.cardAction({
      action: "save",
      target: "activity",
      edit_id: item.id,
      edit_is_active: true,
      edit_is_favorite: !isFavorite,
      edit_name: item.name,
    });
  };

  const editActivity = () => {
    //using push to force changing the page if already there
    if (navigation) {
      navigation.push("edit_activity", {
        selectedPage: "rn:edit_activity",
        args: {
          activity: getActivityModel(item),
        },
      });
    } else {
      controller.changePage("feed", {
        selectedPage: "rn:edit_activity",
        args: {
          activity: getActivityModel(item),
        },
      });
    }
  };

  const onArchive = (activity) => {
    if (isWeb) {
      confirmedArchive();
      return;
    }

    Alert.alert(
      "Archive activity",
      "Are you sure you want to remove this activity?",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "OK",
          onPress: confirmedArchive,
        },
      ],
      { cancelable: true },
    );
  };

  const confirmedArchive = () => {
    setTimeout(() => {
      setIsArchived(true);
    }, 3000);

    controller.tracking.track("Archive Activity");
    console.log("Archive Activity");
    controller.cardActionWithCallback(
      {
        action: "save",
        target: "activity",
        edit_id: item.id,
        edit_name: item.name,
        edit_is_active: false,
      },
      () => {
        controller.cardAction({ action: "refresh" });
      },
    );
  };

  return (
    <Swipeable
      rightButtons={[
        <TouchableOpacity
          style={[styles.buttonsContainer, styles.tags]}
          onPress={() => editActivity()}
        >
          <Text style={styles.buttonText}>Edit</Text>
        </TouchableOpacity>,
        <TouchableOpacity
          style={[styles.buttonsContainer, styles.archive]}
          onPress={() => onArchive(item)}
        >
          <Text style={styles.buttonText}>Archive</Text>
        </TouchableOpacity>,
      ]}
      onRightButtonsOpenRelease={onOpen}
      onRightButtonsCloseRelease={onClose}
    >
      {!isArchived ? (
        <View style={[styles.listItem, isHighlighted && appStyles.highlight]}>
          <TouchableOpacity
            style={styles.row}
            onLongPress={onLongPress}
            onPress={onActivitySelectedHandler}
          >
            <ImageComponent styles={styles.activityLogo} icon={item.icon} />

            <View>
              <Text style={styles.itemName}>{item.name}</Text>
              {/* Tags */}
              <ActivityTags
                activity={item}
                controller={controller}
                showAll={showAllTags}
              />
            </View>
          </TouchableOpacity>

          <View
            style={[
              styles.icons,
              !item.auto_fill_source && { marginRight: "3%" },
            ]}
          >
            <Icons.Favorite
              isFavorite={isFavorite}
              toggleFavoriteState={toggleFavoriteState}
            />
            <ActivitySource
              source={item.auto_fill_source}
              onPress={() => onPredictionIconPressed(item.auto_fill_source)}
            />

            {isSelectable ? (
              <TouchableOpacity style={styles.checkbox}>
                <RoundCheckbox
                  size={20}
                  checked={isSelected}
                  onValueChange={onActivitySelectedHandler}
                />
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      ) : null}
    </Swipeable>
  );
};

export default memo(ActivityItem);

const styles = StyleSheet.create({
  listItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minHeight: 44,
    marginLeft: "3%",
    borderBottomColor: "#F5F5F5",
    borderBottomWidth: 1,
  },
  row: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    flex: 1,
  },
  itemName: {
    fontFamily:  Platform.select({ default: "Montserrat", android: "Montserrat-SemiBold" }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: "#2D3142",
    paddingLeft: 8,
  },
  activityLogo: {
    width: 30,
    height: 30,
  },
  icons: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 60,
    marginRight: 20,
  },
  buttonsContainer: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    paddingLeft: 10,
  },
  tags: {
    backgroundColor: "#F2994A",
  },
  archive: {
    backgroundColor: "#F34213",
  },
  buttonText: {
    color: "#FFF",
    fontFamily:  Platform.select({ default: "Nunito", android: "Nunito-SemiBold" }),
    fontWeight: "bold",
    fontSize: 15,
    lineHeight: 20,
  },
  checkbox: {
    marginLeft: 20,
    marginRight: 20,
  },
});
